/**
 * @module
 *
 * @import {Controller} from '@acng/frontend-stargazer'
 */
import {CONNECT} from '@acng/frontend-stargazer';
import {on, INPUT, KEYDOWN} from '@acng/frontend-bounty/dom/event.js';
import {guard, IS, REGEX} from '@acng/frontend-rubicon';
import {isDefined} from '@acng/frontend-bounty/std/value.js';
import {set} from '@acng/frontend-bounty/dom/attribute.js';
import {query, queryAll} from '@acng/frontend-bounty/dom/query.js';
import {BUTTON} from '@acng/frontend-bounty/dom/element.js';
import {iterate} from '@acng/frontend-bounty/object.js';
import {SubmitEventController} from 'nova';

/**
 * @implements {Controller}
 */
class OneTimeCodeFormController extends SubmitEventController {
  /**
   * @callback OneTimeCodeAction
   *
   * @param {HTMLFormElement} form
   * @param {string} code
   *
   * @returns {unknown}
   */

  /**
   * @param {OneTimeCodeAction} action
   */
  constructor(action) {
    super(async (form) => {
      const code = OneTimeCodeFormController.#getInputCode(form);
      ASSERT: guard(code, REGEX(/^\d{6,6}$/), 'two factor authentication code');
      await action(form, code);
      form.reset();
    });
  }

  /**
   * Upgrade all `<input>`. Responsive `<button>`
   *
   * @param {Element} element
   *
   * @override
   */
  [CONNECT](element) {
    super[CONNECT](element);

    const button = query(element, BUTTON);
    ASSERT: guard(button, IS(HTMLButtonElement));

    button.disabled = true;

    on(element, INPUT, () => {
      const value = OneTimeCodeFormController.#getInputCode(element);
      button.disabled = value.length != 6;
    });

    OneTimeCodeFormController.#upgradeAllInputElements(element);
  }

  /**
   * @param {Element} form
   *
   * @returns {string} Six decimal digits
   */
  static #getInputCode(form) {
    let code = '';
    iterate(queryAll(form, INPUT), (input) => (code += input.value));

    return code;
  }

  /**
   * @param {ParentNode} parentNode
   *
   * @returns {NodeListOf<HTMLInputElement>}
   */
  static #upgradeAllInputElements(parentNode) {
    const inputList = queryAll(parentNode, INPUT);
    inputList.forEach(OneTimeCodeFormController.#upgradeInput);

    return inputList;
  }

  /**
   * @param {HTMLInputElement} input
   * @param {number} index
   * @param {NodeListOf<HTMLInputElement> | HTMLInputElement[]} list
   */
  static #upgradeInput(input, index, list) {
    /**
     * @type {HTMLInputElement | undefined}
     */
    const prevInput = list[index - 1];

    /**
     * @type {HTMLInputElement | undefined}
     */
    const nextInput = list[index + 1];

    set(input, 'type', 'number');
    set(input, 'autocomplete', index ? 'off' : 'one-time-code');
    set(input, 'name', `code`);
    set(input, 'required');
    set(input, 'min', '0');
    set(input, 'max', '9');

    on(input, KEYDOWN, ({code}) => {
      if (code == 'Backspace' && input.value == '') {
        prevInput?.focus();
      }
    });

    on(input, INPUT, () => {
      const [first, ...rest] = input.value;
      input.value = first ?? '';

      if (isDefined(first) && nextInput) {
        nextInput.focus();
        nextInput.value = rest.join('');
        nextInput.dispatchEvent(new InputEvent(INPUT));
      }
    });
  }
}

export {OneTimeCodeFormController};
